import React from 'react';
import {Node, Edge, useNodes, useEdges, useReactFlow} from 'reactflow';

import {FlowchartData} from '../layout/algorithms';
import {LayoutSettings} from '../layout/calculateDimensionsForLayout';
import layoutFlowchart from '../layout/layoutFlowchart';
import {getFitViewAnimationDurationMs} from '../ReactFlowConstants';
import BaseControlButton from './BaseControlButton';

interface ControlledAutoLayoutButtonProps<N extends Node, E extends Edge> {
  flowchartData: FlowchartData<N, E>;
  customLayoutSettings?: Partial<LayoutSettings<N, E>>;
  setNodes: (nodes: Array<N>) => void;
  setEdges: (edges: Array<E>) => void;
  disabled?: boolean;
}

export function ControlledAutoLayoutButton<N extends Node, E extends Edge>(
  props: ControlledAutoLayoutButtonProps<N, E>
): JSX.Element {
  const {flowchartData, customLayoutSettings, disabled} = props;
  const {fitView} = useReactFlow();

  return (
    <BaseControlButton
      icon="grid-two"
      tooltip="Auto layout"
      onClick={async () => {
        const {nodes, edges} = await layoutFlowchart(flowchartData, customLayoutSettings);
        props.setNodes(nodes);
        props.setEdges(edges);
        // Defer the fitView to the next tick to ensure that the nodes have been rendered and measured.
        setTimeout(() => {
          fitView({duration: getFitViewAnimationDurationMs()});
        });
      }}
      dataTestAttrs={{component: 'ControlledAutoLayoutButton', element: 'root'}}
      disabled={disabled}
    />
  );
}

export function UncontrolledAutoLayoutButton<N extends Node, E extends Edge>(props: {
  customLayoutSettings?: Partial<LayoutSettings<N, E>>;
}): JSX.Element {
  const nodes = useNodes();
  const edges = useEdges();
  const {setNodes, setEdges} = useReactFlow();

  return (
    <ControlledAutoLayoutButton
      flowchartData={{nodes, edges}}
      customLayoutSettings={props.customLayoutSettings}
      setNodes={setNodes}
      setEdges={setEdges}
    />
  );
}
